import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import { getTopbarDateChicago } from "../utils/formatDate";
import SecuritySeal from "./SecuritySeal";
import { Helmet } from "react-helmet-async";
const navItems = [
  { label: "Dashboard", icon: "📊", path: "/dashboard", section: "MAIN" },
  { label: "Customers", icon: "👥", path: "/customers", section: "OPERATIONS" },
  {
    label: "Cash a Check",
    icon: "💵",
    path: "/cheques/new",
    section: "OPERATIONS",
  },
  //{ label: "Cash a Check New",   icon: "💵",  path: "/cheques/new2",    section: "OPERATIONS",  },
  // { label: 'Demo Cash a Check', icon: '🧪', path: '/cheques/demo',      section: 'OPERATIONS' },
  //{  label: "Dual Camera", icon: "📷",   path: "/dual-camera",  section: "OPERATIONS",  },
  {
    label: "Transactions",
    icon: "📋",
    path: "/cheques",
    section: "OPERATIONS",
  },
  {
    label: "Blocked Customers",
    icon: "🚫",
    path: "/blocked-customers",
    section: "OPERATIONS",
  },
  { label: "Banks", icon: "🏦", path: "/banks", section: "MASTERS" },
  { label: "Companies", icon: "🏢", path: "/companies", section: "MASTERS" },
  { label: "Reports", icon: "📈", path: "/reports", section: "REPORTS" },
];

/**
 * Layout Component.
 * Main application shell wrapper containing responsive sidebar navigation, topbar header with Chicago time, and page container.
 *
 * @component
 * @param {Object} props - React component props.
 * @param {React.ReactNode} props.children - Page view content wrapped inside layout shell.
 * @param {string} [props.title] - Document and topbar title.
 * @returns {JSX.Element} App layout shell.
 */
export default function Layout({ children, title }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    Swal.fire({
      title: "Confirm Logout",
      text: "Are you sure you want to log out of SecureCheck?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, Logout",
      cancelButtonText: "No, Stay",
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        logout();
        toast.success("Logged out successfully");
        navigate("/login");
      }
    });
  };

  const initials =
    user?.name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2) || "U";

  let lastSection = "";

  return (
    <div className="app-layout">
      <Helmet>
        <title>{title ? `${title} | SecureCheck` : 'SecureCheck'}</title>
        <meta name="description" content="SecureCheck Cashing System - The premier cloud-based check cashing and compliance software." />
        <meta name="keywords" content="check cashing, securecheck, compliance, pos, point of sale, money service business, msb software" />
      </Helmet>

      {/* Mobile/tablet sidebar overlay */}
      {sidebarOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? "sidebar-open" : ""}`}>
        <div className="sidebar-logo">
          <div className="logo-icon">$</div>
          <div className="logo-text">
            Secure<span>Check</span>
            <br />
            <span style={{ fontSize: 10, opacity: 0.5, fontWeight: 400 }}>
              Cashing System
            </span>
          </div>
        </div>

        <nav className="sidebar-nav">
          {navItems.map((item) => {
            const showSection = item.section !== lastSection;
            lastSection = item.section;
            return (
              <React.Fragment key={item.path}>
                {showSection && (
                  <div className="nav-section-title">{item.section}</div>
                )}
                <NavLink
                  to={item.path}
                  className={({ isActive }) =>
                    `nav-item ${isActive ? "active" : ""}`
                  }
                  onClick={() => setSidebarOpen(false)}
                >
                  <span className="nav-icon">{item.icon}</span>
                  {item.label}
                </NavLink>
              </React.Fragment>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <div className="user-chip">
            <div className="user-avatar">{initials}</div>
            <div className="user-info">
              <div className="user-name">{user?.name || "User"}</div>
              <div className="user-role">{user?.role}</div>
            </div>
            <button
              onClick={handleLogout}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "rgba(255,255,255,0.4)",
                fontSize: 18,
                padding: 4,
              }}
              title="Logout"
            >
              ⏻
            </button>
          </div>
          <SecuritySeal variant="compact" style={{ marginTop: 10 }} />
        </div>
      </aside>

      {/* Main */}
      <div className="main-content">
        <header className="topbar">
          {/* ── Left: hamburger + page title ── */}
          <div className="topbar-left">
            <button
              className="sidebar-hamburger"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-label="Toggle sidebar"
            >
              ☰
            </button>
            <div className="topbar-divider" />
            <div className="topbar-title">{title}</div>
          </div>

          {/* ── Right: date · user chip · logout ── */}
          <div className="topbar-right">
            {/* Date pill */}
            <div className="topbar-date">
              <span className="topbar-date-icon">📅</span>
              <span>{getTopbarDateChicago()}</span>
            </div>

            {/* Slim separator */}
            <div className="topbar-divider" />

            {/* User info chip */}
            <div className="topbar-user-chip">
              <div className="user-avatar" style={{ width: 32, height: 32, fontSize: 11 }}>
                {initials}
              </div>
              <div className="topbar-user-info">
                <span className="topbar-user-name">{user?.name || "User"}</span>
                <span className="topbar-user-role">{user?.role}</span>
              </div>
            </div>

            {/* Logout icon button */}
            <button
              onClick={handleLogout}
              aria-label="Logout"
              title="Logout"
              className="topbar-logout-btn"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
              <span>Logout</span>
            </button>
          </div>
        </header>

        <main className="page-content">{children}</main>
      </div>
    </div>
  );
}
